import { mapPasskeyBeErrorCodeOrUnknown } from '@modules/passkey/helpers/passkey-error.helper';
import { isPasskeyPlatformApiConfigured } from '@modules/passkey/helpers/passkey-api-config.helper';
import { createPasskeyPlatformApiService, PasskeyPlatformApiError } from './passkey-platform-api.client';
import type { PasskeyApiService } from '@modules/passkey/interfaces/passkey-api.interface';
import type { PasskeyCallbackReason } from '@modules/passkey/interfaces/passkey-callback.interface';
import type { PasskeyPlatformRequestContext } from '@modules/passkey/helpers/passkey-api-config.helper';

class PasskeyApiNotConfiguredError extends Error {
  constructor() {
    super('Passkey BE API contract is not configured');
    this.name = 'PasskeyApiNotConfiguredError';
  }
}

const createFailClosedPasskeyApiService = (): PasskeyApiService => {
  const fail = () => Promise.reject(new PasskeyApiNotConfiguredError());

  return {
    beginAuthenticate: fail,
    verifyForgotPassword: fail,
  };
};

export const createPasskeyApiService = (context: PasskeyPlatformRequestContext = {}): PasskeyApiService => {
  if (isPasskeyPlatformApiConfigured()) {
    return createPasskeyPlatformApiService(context);
  }

  return createFailClosedPasskeyApiService();
};

const mapDomExceptionReason = (error: DOMException): PasskeyCallbackReason | undefined => {
  if (error.name === 'NotAllowedError') {
    return 'userRejected';
  }

  if (error.name === 'SecurityError') {
    return 'unsupportedDevice';
  }

  if (error.name === 'InvalidStateError') {
    return 'duplicateCredential';
  }

  return undefined;
};

export const getPasskeyApiReason = (error: unknown): PasskeyCallbackReason => {
  if (error instanceof PasskeyApiNotConfiguredError) {
    return 'apiNotConfigured';
  }

  if (error instanceof PasskeyPlatformApiError) {
    if (error.code > 0) {
      return mapPasskeyBeErrorCodeOrUnknown(error.code);
    }

    return 'verifyFailed';
  }

  if (error instanceof DOMException) {
    return mapDomExceptionReason(error) ?? 'unknown';
  }

  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = Number((error as { code: unknown }).code);
    if (!Number.isNaN(code)) {
      return mapPasskeyBeErrorCodeOrUnknown(code);
    }
  }

  return 'unknown';
};
